import { useQuery } from '@tanstack/react-query';
import { appelApi } from './api';
import type { DemiJournee, StatutPresence } from './useEmargement';

/**
 * Fiche d'assiduité d'un dossier (CDC §6.6, §6.7).
 *
 * Les chiffres viennent tous de l'API : le taux de présence, le cumul face au
 * seuil et la tendance sont calculés par `domain/absences.ts`, la même source
 * que l'écran des absences et le PV d'émargement. Les recalculer ici ferait
 * trois chiffres pour une seule réalité.
 */

export type TypeAbsence = 'MATIN' | 'APRES_MIDI' | 'JOURNEE_COMPLETE';

export interface LigneAssiduite {
  presenceId: string;
  seanceId: string;
  jour: string;
  jourLisible: string;
  demiJournee: DemiJournee;
  demiJourneeLibelle: string;
  seance: string;
  formation: string | null;
  session: string | null;
  statut: StatutPresence;
  statutLibelle: string;
  statutTon: string;
  justifiee: boolean;
  motifJustification: string | null;
  signeLe: string | null;
  appelPar: string | null;
  modifiePar: string | null;
  modifieLe: string;
}

export interface FicheAssiduite {
  apprenantId: string;
  apprenant: string;
  formation: string | null;
  session: string | null;
  siteCode: string;
  statutDossier: string;
  synthese: {
    convoquees: number;
    emargees: number;
    presents: number;
    retards: number;
    absences: number;
    absencesJustifiees: number;
    attendues: number;
    tauxPresence: number | null;
    signatures: number;
  };
  cumul: {
    total: number;
    injustifiees: number;
    justifiees: number;
    seuil: number;
    seuilDepasse: boolean;
    margeAvantSeuil: number;
  };
  journeesAbsentes: {
    jour: string;
    jourLisible: string;
    type: TypeAbsence;
    justifiee: boolean;
  }[];
  tendance: {
    fenetreJours: number;
    emargees: number;
    absences: number;
    tauxPresence: number | null;
  };
  lignes: LigneAssiduite[];
  /**
   * Taux d'absentéisme depuis le début du parcours, feuilles clôturées seules,
   * face au seuil de l'organisme (TF-427, TF-461). `null` pour l'apprenant.
   */
  absenteisme: {
    taux: number | null;
    demiJournees: number;
    absences: number;
    justifiees: number;
    seuil: number;
    auDelaDuSeuil: boolean;
  } | null;
}

export function useAssiduite(apprenantId: string | undefined) {
  return useQuery({
    queryKey: ['assiduite', apprenantId ?? ''],
    queryFn: () => appelApi<FicheAssiduite>(`/emargement/assiduite/${apprenantId}`),
    enabled: Boolean(apprenantId),
  });
}
